'use client';

import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  COUNTRY_CODE_US,
  CountryCode,
} from '@sola/schema/dotcom/shared/geo.ts';

import { usePhoneMask } from '../../utils/masks.ts';
import { InputField, InputFieldProps } from '../input-field/input-field.tsx';

export type PhoneFieldProps = InputFieldProps & {
  countryCode: CountryCode;
};

// TODO: Right now we're loading the mask deps when field is in viewport.
// This can prob be optimized so that field deps are only lazy loaded when
// form is interacted with. We can't to this on a field level since inputs can
// be autofilled before masks are applied.
export const PhoneField: React.FC<PhoneFieldProps> = ({
  countryCode = COUNTRY_CODE_US,
  ...fieldProps
}) => {
  const { mask, loadMask } = usePhoneMask(countryCode);
  const { t } = useTranslation(['forms']);
  const { name } = fieldProps;
  const { trigger, formState } = useFormContext();

  // Trigger validation when the countryCode changes
  useEffect(() => {
    if (!name) {
      return;
    }
    if (!formState.touchedFields[name]) {
      return;
    }
    trigger(name);
  }, [countryCode, name]);

  return (
    <InputField
      type="tel"
      placeholder={mask?.placeholder}
      autoComplete="tel"
      onVisible={loadMask}
      // Collapse digit-less input (e.g. leftover "(") to '' so junk isn't
      // submitted. Kept here because ark-my has no normalization of its own.
      setValueAs={(value?: string) => (value?.replace(/\D/g, '') ? value : '')}
      // TODO we should investigate if there is a smooth way to bypass masked values when validating
      validate={(maskedValue) => {
        // Maskito is the primary guard on shape; Judge emptiness by digits so
        // leftover mask chars (e.g. a lone "(") count as empty..
        const digits = (maskedValue ?? '').replace(/\D/g, '');
        if (countryCode !== 'US') {
          return true;
        }
        if (!fieldProps.required && digits.length === 0) {
          return true;
        }
        return digits.length === 10 ? true : t('forms:errors.invalidPhone');
      }}
      {...fieldProps}
      {...(!!mask && { maskOptions: mask.options })}
    />
  );
};
